import type { Lists } from '@ts-lists/interfaces'
import type { ListsStore } from './interfaces'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { TutorBillingAccountType } from '@ts-lists/enums'

import { tasksKanban } from '@utils/stores/lists/getters/tasks-kanban'

const mapRecordToOptions = (record: Record<string, string> | null | undefined): AppSelectOption[] => {
  if (!record) return []
  return Object.entries(record).map(([value, text]) => ({ value, text }))
}

const getTutor = (state: ListsStore): Lists['tutor'] | null => state.lists?.tutor || null
const getTutorStatusOptions = (state: ListsStore): AppSelectOption[] => mapRecordToOptions(getTutor(state)?.status)
const getTutorSlot = (state: ListsStore): Lists['tutor_slot'] | null => state.lists?.tutor_slot || null
const getTutorSlotType = (state: ListsStore): Lists['tutor_slot']['type'] | null => getTutorSlot(state)?.type || null
const getTutorSlotTypeOptions = (state: ListsStore): AppSelectOption[] => mapRecordToOptions(getTutorSlotType(state))
const getTutorSlotStatusOptions = (state: ListsStore): AppSelectOption[] => mapRecordToOptions(getTutorSlot(state)?.status)
const getTutorSlotDemoStatusOptions = (state: ListsStore): AppSelectOption[] => mapRecordToOptions(getTutorSlot(state)?.demo_status)

const getTutorBilling = (state: ListsStore): Lists['tutor_billing'] | null => state.lists?.tutor_billing || null
const getTutorBillingDocumentTypeOptions = (state: ListsStore): AppSelectOption[] =>
  mapRecordToOptions(getTutorBilling(state)?.document_type)
const getTutorBillingPaymentServiceOptions = (state: ListsStore): AppSelectOption[] =>
  mapRecordToOptions(getTutorBilling(state)?.payment_service)
const getTutorBillingAccountType = (state: ListsStore) => (service: string): TutorBillingAccountType | null =>
  getTutorBilling(state)?.payment_service_account_type?.[service] ?? null
const getTutorBillingExportTypeOptions = (state: ListsStore): AppSelectOption[] =>
  mapRecordToOptions(getTutorBilling(state)?.export_type)
const getTutorBillingPaymentServiceLabel = (state: ListsStore) => (value: string | null): string =>
  (value && getTutorBilling(state)?.payment_service?.[value]) || value || '—'

export const getters = {
  hasData: (state: ListsStore): boolean => !!state.lists,
  getLists: (state: ListsStore): ListsStore['lists'] => state.lists,
  getDiscipline: (state: ListsStore): Lists['discipline'] | null => state.lists?.discipline || null,
  getTutor: (state: ListsStore): Lists['tutor'] | null => state.lists?.tutor || null,
  getTutorSlot: (state: ListsStore): Lists['tutor_slot'] | null => getTutorSlot(state),
  getTutorSlotType: (state: ListsStore): Lists['tutor_slot']['type'] | null => getTutorSlotType(state),
  isTutorSlotType: (state: ListsStore): boolean => !!getTutorSlotType(state),
  getTutorSlotTypeOptions: (state: ListsStore): AppSelectOption[] => getTutorSlotTypeOptions(state),
  getTutorSlotStatusOptions: (state: ListsStore): AppSelectOption[] => getTutorSlotStatusOptions(state),
  getTutorSlotDemoStatusOptions: (state: ListsStore): AppSelectOption[] => getTutorSlotDemoStatusOptions(state),
  getTutorStatusOptions: (state: ListsStore): AppSelectOption[] => getTutorStatusOptions(state),
  getTutorBillingDocumentTypeOptions: (state: ListsStore): AppSelectOption[] => getTutorBillingDocumentTypeOptions(state),
  getTutorBillingPaymentServiceOptions: (state: ListsStore): AppSelectOption[] => getTutorBillingPaymentServiceOptions(state),
  getTutorBillingAccountType: (state: ListsStore): ((service: string) => TutorBillingAccountType | null) =>
    getTutorBillingAccountType(state),
  getTutorBillingExportTypeOptions: (state: ListsStore): AppSelectOption[] => getTutorBillingExportTypeOptions(state),
  getTutorBillingPaymentServiceLabel: (state: ListsStore): ((value: string | null) => string) =>
    getTutorBillingPaymentServiceLabel(state),
  ...tasksKanban,
}
